import { SlideConfig } from '../types';

/**
 * Mapping da slide ID a benefit config key
 * decision_stage mostra il beneficio per 'install_timeline'
 */
export const SLIDE_TO_BENEFIT_KEY: Record<string, string> = {
  decision_stage: 'install_timeline',
  name_form: 'name_form',
  email_form: 'email_form',
  phone_form: 'phone_form',
};

/**
 * Configurazione delle slide del wizard SliderVpp
 *
 * Slide types:
 * - SINGLE_ANSWER: Selezione singola con griglia di opzioni
 * - FORM_INPUT: Form con input fields
 * - INFO: Slide informativa (loading, success, error)
 */
// SVG data URI — spunta verde
const ICON_YES =
  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Ccircle cx='12' cy='12' r='12' fill='%2322c55e'/%3E%3Cpath fill='%23fff' d='M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z'/%3E%3C/svg%3E";

// SVG data URI — X rossa
const ICON_NO =
  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Ccircle cx='12' cy='12' r='12' fill='%23ef4444'/%3E%3Cpath fill='%23fff' d='M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z'/%3E%3C/svg%3E";

export const SLIDES_CONFIG: SlideConfig[] = [
  // ============================================
  // SLIDE 0: Impianto esistente?
  // ============================================
  {
    id: 'has_existing_system',
    type: 'SINGLE_ANSWER',
    title: 'Hai già un impianto fotovoltaico o una pompa di calore?',
    apiFieldName: 'has_existing_system',
    autoAdvance: true,
    answers: [
      { label: 'Sì', value: 'yes', image: ICON_YES },
      { label: 'No', value: 'no', image: ICON_NO },
    ],
  },

  // ============================================
  // SLIDE 0b: Come vuoi entrare in Enpal? (solo se ha impianto esistente)
  // ============================================
  {
    id: 'enpal_entry_type',
    type: 'SINGLE_ANSWER',
    title: 'Ok, come vuoi entrare in Enpal?',
    apiFieldName: 'enpal_entry_type',
    autoAdvance: true,
    condition: (formData) => formData.has_existing_system === 'yes',
    answers: [
      {
        label: 'Rifacimento/Ampliamento impianto',
        value: 'system_replacement',
        image: 'https://cdn.prod.website-files.com/6621fcb456e6148dbbc48ea9/6621fcb456e6148dbbc490ee_IT_Slider_Icons_fotovoltaico_con_pompa_di_calore.svg',
      },
      {
        label: 'Enpal One: rivendita energia a prezzi maggiorati',
        value: 'energy_resale_vpp',
        image: 'https://uploads-ssl.webflow.com/62da8209945d9e1b7e7daf07/65a690341ab4c8ae3331bda7_IT_Slider_Icons_fotovoltaico.svg',
      },
    ],
  },

  // ============================================
  // CER FLOW — visibile solo quando enpal_entry_type === 'cer_producer'
  // ============================================

  // CER 1: Intro CER
  {
    id: 'cer_intro',
    type: 'INFO',
    title: 'CER Enpal — Adesione come produttore di energia',
    html: `<p>Benvenuto nel flusso di adesione alla <strong>Comunità Energetica Rinnovabile Enpal</strong>.</p>
<p style="margin-top:0.75rem">Prima di procedere ti invitiamo a prendere visione dello
<a href="https://drive.google.com/file/d/1oM1Tvym0Th3KLiqG3pl5_2O9KVU7kUE2/view?usp=drive_link" target="_blank" rel="noopener noreferrer"><strong>Statuto</strong></a>
e del
<a href="https://drive.google.com/file/d/1RnmakOL5dNNL_LZFGsDADV8FwABP44ee/view?usp=drive_link" target="_blank" rel="noopener noreferrer"><strong>Regolamento</strong></a>
di CER Enpal.</p>`,
    condition: (formData) => formData.enpal_entry_type === 'cer_producer',
  },

  // CER 2: Dati di contatto + IBAN
  {
    id: 'cer_contact',
    type: 'FORM_INPUT',
    title: 'I tuoi dati di contatto',
    condition: (formData) => formData.enpal_entry_type === 'cer_producer',
    inputs: [
      { name: 'name', placeholder: 'Nome e Cognome', type: 'text', required: true },
      { name: 'email', placeholder: 'Indirizzo email', type: 'email', required: true },
      { name: 'phone', placeholder: 'Numero di telefono', type: 'tel', required: true },
      { name: 'cer_iban', placeholder: 'IBAN (es. IT12X1234512345000000123456)', type: 'text', required: true },
    ],
  },

  // CER 3: Tipologia giuridica
  {
    id: 'cer_legal_type',
    type: 'SINGLE_ANSWER',
    title: 'Tipologia giuridica dell\'intestatario',
    apiFieldName: 'cer_legal_type',
    autoAdvance: true,
    condition: (formData) => formData.enpal_entry_type === 'cer_producer',
    answers: [
      { label: 'Persona fisica', value: 'persona_fisica' },
      { label: 'Persona giuridica', value: 'persona_giuridica' },
    ],
  },

  // CER 4: Indirizzo dell'impianto
  {
    id: 'cer_address',
    type: 'FORM_INPUT',
    title: 'Indirizzo dell\'impianto (dati toponomastici)',
    condition: (formData) => formData.enpal_entry_type === 'cer_producer',
    inputs: [
      { name: 'cer_address_via', placeholder: 'Via/Piazza e numero civico (es. Via Roma 1)', type: 'text', required: true },
      { name: 'cer_address_city', placeholder: 'Città', type: 'text', required: true },
      { name: 'cer_address_cap', placeholder: 'CAP', type: 'tel', required: true, pattern: '^\\d{5}$' },
    ],
  },

  // CER 5: Dati impianto fotovoltaico
  {
    id: 'cer_plant_data',
    type: 'FORM_INPUT',
    title: 'Dati relativi all\'impianto fotovoltaico',
    condition: (formData) => formData.enpal_entry_type === 'cer_producer',
    inputs: [
      { name: 'cer_plant_power', placeholder: 'Potenza impianto (kWp, solo valore numerico)', type: 'text', required: true },
      { name: 'cer_modules_qty', placeholder: 'Quantità moduli fotovoltaici', type: 'text', required: true },
      { name: 'cer_modules_brand', placeholder: 'Marca moduli fotovoltaici', type: 'text', required: true },
      { name: 'cer_modules_model', placeholder: 'Modello moduli fotovoltaici', type: 'text', required: true },
      { name: 'cer_modules_power', placeholder: 'Potenza moduli fotovoltaici (kWp, solo valore numerico)', type: 'text', required: true },
      { name: 'cer_rc_code', placeholder: 'Codice RC richiesta Terna da Gaudì', type: 'text', required: true },
    ],
  },

  // CER 6: Sistema di accumulo
  {
    id: 'cer_battery',
    type: 'SINGLE_ANSWER',
    title: 'L\'impianto presenta sistema di accumulo?',
    apiFieldName: 'cer_battery',
    autoAdvance: true,
    condition: (formData) => formData.enpal_entry_type === 'cer_producer',
    answers: [
      { label: 'Sì', value: 'yes', image: ICON_YES },
      { label: 'No', value: 'no', image: ICON_NO },
    ],
  },

  // CER 7: Consensi (renderizzato come componente custom CerConsentSlide)
  {
    id: 'cer_consent',
    type: 'INFO',
    title: '',
    condition: (formData) => formData.enpal_entry_type === 'cer_producer',
  },

  // CER 8: Riepilogo documenti da caricare
  {
    id: 'cer_documents',
    type: 'INFO',
    title: '',
    condition: (formData) => formData.enpal_entry_type === 'cer_producer',
  },

  // ============================================
  // FLUSSO PRINCIPALE — nascosto per cer_producer
  // ============================================

  // SLIDE 1: Selezione prodotto
  // ============================================
  {
    id: 'customer_type',
    type: 'SINGLE_ANSWER',
    title: 'Scopri i bonus per il prodotto che devi sostituire o ampliare',
    apiFieldName: 'product_type',
    autoAdvance: true,
    condition: (formData) =>
      formData.enpal_entry_type !== 'cer_producer' &&
      formData.enpal_entry_type !== 'energy_resale_vpp',
    answers: [
      {
        label: 'Fotovoltaico',
        value: 'Photovoltaic',
        image: 'https://uploads-ssl.webflow.com/62da8209945d9e1b7e7daf07/65a690341ab4c8ae3331bda7_IT_Slider_Icons_fotovoltaico.svg',
      },
      {
        label: 'Pompa di Calore',
        value: 'Heatpump',
        image: 'https://uploads-ssl.webflow.com/62da8209945d9e1b7e7daf07/65a69034ad376843aacab5d2_IT_Slider_Icons_pompa_di_calore.svg',
      },
      {
        label: 'Fotovoltaico + Pompa di Calore',
        value: 'Photovoltaic + Heatpump',
        image: 'https://cdn.prod.website-files.com/6621fcb456e6148dbbc48ea9/6621fcb456e6148dbbc490ee_IT_Slider_Icons_fotovoltaico_con_pompa_di_calore.svg',
      },
    ],
  },

  // ============================================
  // SLIDE 2: Timeline installazione (NO header)
  // ============================================
  {
    id: 'install_timeline',
    type: 'SINGLE_ANSWER',
    title: 'Alcuni bonus hanno scadenza limitata, entro quando vorresti installare?',
    apiFieldName: 'purchase_time',
    autoAdvance: true,
    condition: (formData) =>
      formData.enpal_entry_type !== 'cer_producer' &&
      formData.enpal_entry_type !== 'energy_resale_vpp',
    answers: [
      { label: 'Entro 1-2 mesi', value: 'one_to_two_months' },
      { label: 'Entro 3-4 mesi', value: 'three_to_four_months' },
      { label: 'Fra + di 5 mesi', value: 'more_than_5_months' },
      { label: 'Non ho una scadenza precisa', value: 'dont_know' },
    ],
  },

  // ============================================
  // SLIDE 3: Tipo edificio (NO header)
  // ============================================
  {
    id: 'house_type',
    type: 'SINGLE_ANSWER',
    title: 'Per quale tipo di edificio vuoi scoprire i bonus?',
    apiFieldName: 'house_type',
    autoAdvance: true,
    condition: (formData) =>
      formData.enpal_entry_type !== 'cer_producer' &&
      formData.enpal_entry_type !== 'energy_resale_vpp',
    answers: [
      {
        label: 'Casa a schiera',
        value: 'townhouse',
        image: 'https://uploads-ssl.webflow.com/62da8209945d9e1b7e7daf07/65a69035fd81055cfcf886db_IT_Slider_Icons_casa_a_schiera.svg',
      },
      {
        label: 'Casa unifamiliare',
        value: 'detached_house',
        image: 'https://uploads-ssl.webflow.com/62da8209945d9e1b7e7daf07/65a690347c3338223a5c8d5d_IT_Slider_Icons_casa_unifamiliare.svg',
      },
      {
        label: 'Palazzina (fino a 6 famiglie)',
        value: 'small_building',
        image: 'https://cdn.prod.website-files.com/6621fcb456e6148dbbc48ea9/672101f39fd017299f37df74_IT_Slider_Icons_Copropriet%C3%A0.svg',
      },
      {
        label: 'Condominio (più di 6 famiglie)',
        value: 'multi_family_house',
        image: 'https://cdn.prod.website-files.com/6621fcb456e6148dbbc48ea9/672101f43a7562ecc9146c47_IT_Slider_Icons_Condominio.svg',
      },
      {
        label: 'Edificio industriale / Capannone',
        value: 'commercial_building',
        image: 'https://uploads-ssl.webflow.com/62da8209945d9e1b7e7daf07/65a69035e97d173f90bee987_IT_Slider_Icons_edificio_commerciale.svg',
      },
      {
        label: 'Pergolati & Altro',
        value: 'other',
        image: 'https://cdn.prod.website-files.com/6621fcb456e6148dbbc48ea9/672101f3dffe24cc4b89bb48_IT_Slider_Icons_Pergolati%20e%20altro.svg',
      },
    ],
  },

  // ============================================
  // SLIDE 4: Form CAP (NO header)
  // ============================================
  {
    id: 'location_form',
    type: 'FORM_INPUT',
    title: 'Scopri gli incentivi e bonus nella tua zona',
    condition: (formData) =>
      formData.enpal_entry_type !== 'cer_producer' &&
      formData.enpal_entry_type !== 'energy_resale_vpp',
    inputs: [
      {
        name: 'zip',
        placeholder: 'Il tuo CAP',
        type: 'tel',
        required: true,
        pattern: '^\\d{5}$',
      },
    ],
  },

  // ============================================
  // SLIDE 5: Fase decisionale (BENEFICIO 1/5)
  // Mostra il beneficio per 'install_timeline'
  // ============================================
  {
    id: 'decision_stage',
    type: 'SINGLE_ANSWER',
    title: 'A che punto sei nella tua decisione di acquisto?',
    apiFieldName: 'pv_interest_since',
    autoAdvance: true,
    condition: (formData) =>
      formData.enpal_entry_type !== 'cer_producer' &&
      formData.enpal_entry_type !== 'energy_resale_vpp',
    header: {
      savingsSubText: 'BENEFICIO 1/5',
      savingsText: '+9.899€ tramite Ecobonus',
      indicator: { label: '' },
    },
    answers: [
      { label: 'So già che configurazione voglio', value: 'evaluation' },
      { label: 'Sto valutando diversi preventivi', value: 'understand_purchase' },
      { label: 'Sto raccogliendo informazioni', value: 'understand_need' },
      { label: 'Sono solo curioso', value: 'curious' },
    ],
  },

  // ============================================
  // SLIDE VPP: Slide unica nome + email + telefono (solo per energy_resale_vpp)
  // ============================================
  {
    id: 'vpp_contact',
    type: 'FORM_INPUT',
    title: 'Attiva Enpal One sul tuo impianto',
    subtitle: 'Fai trading energetico rivendendo energia nei momenti in cui è pagata di più',
    condition: (formData) => formData.enpal_entry_type === 'energy_resale_vpp',
    inputs: [
      { name: 'name', placeholder: 'Nome e cognome', type: 'text', required: true },
      { name: 'email', placeholder: 'Il tuo indirizzo email', type: 'email', required: true },
      { name: 'phone', placeholder: 'Numero di cellulare', type: 'tel', required: true, pattern: '^[\\d\\s]{10,18}$' },
    ],
  },

  // ============================================
  // SLIDE 6: Form Nome (BENEFICIO 2/5)
  // ============================================
  {
    id: 'name_form',
    type: 'FORM_INPUT',
    title: 'Scopri il prossimo beneficio',
    condition: (formData) =>
      formData.enpal_entry_type !== 'cer_producer' &&
      formData.enpal_entry_type !== 'energy_resale_vpp',
    header: {
      savingsSubText: 'BENEFICIO 2/5',
      savingsText: '+5.400€ tramite Cashback PNRR',
      indicator: { label: '' },
    },
    inputs: [
      {
        name: 'name',
        placeholder: 'Nome e cognome',
        type: 'text',
        required: true,
      },
    ],
  },

  // ============================================
  // SLIDE 7: Form Email (BENEFICIO 3/5)
  // ============================================
  {
    id: 'email_form',
    type: 'FORM_INPUT',
    title: 'Scopri il prossimo beneficio',
    condition: (formData) =>
      formData.enpal_entry_type !== 'cer_producer' &&
      formData.enpal_entry_type !== 'energy_resale_vpp',
    header: {
      savingsSubText: 'BENEFICIO 3/5',
      savingsText: '+3.430€ di Rivendita Energia Annuale',
      indicator: { label: '' },
    },
    inputs: [
      {
        name: 'email',
        placeholder: 'Il tuo indirizzo email',
        type: 'email',
        required: true,
      },
    ],
  },

  // ============================================
  // SLIDE 8: Form Telefono (BENEFICIO 4/5)
  // ============================================
  {
    id: 'phone_form',
    type: 'FORM_INPUT',
    title: '',
    condition: (formData) =>
      formData.enpal_entry_type !== 'cer_producer' &&
      formData.enpal_entry_type !== 'energy_resale_vpp',
    header: {
      savingsSubText: 'BENEFICIO 4/5',
      savingsText: '+759€ di Surplus su rivendita di energia tramite CER',
      indicator: { label: '' },
    },
    inputs: [
      {
        name: 'phone',
        placeholder: 'Numero di cellulare',
        type: 'tel',
        required: true,
        pattern: '^[\\d\\s]{10,18}$',
      },
    ],
  },

  // ============================================
  // SLIDE 9: Loading
  // ============================================
  {
    id: 'loading',
    type: 'INFO',
    title: 'Invio della richiesta in corso...',
    html: '<p>Attendere prego...</p><img src="https://i.gifer.com/origin/34/34338d26023e5515f6cc8969aa027bca.gif" alt="Loading..." style="margin-top:1rem;max-width:80px;border-radius:10px;">',
    condition: (formData) => formData.enpal_entry_type !== 'cer_producer',
  },

  // ============================================
  // SLIDE 10: Success
  // ============================================
  {
    id: 'success',
    type: 'INFO',
    title: '',
    condition: (formData) => formData.enpal_entry_type !== 'cer_producer',
  },

  // ============================================
  // SLIDE 11: Error
  // ============================================
  {
    id: 'error',
    type: 'INFO',
    title: 'Oops, qualcosa è andato storto',
    html: '<p>Si è verificato un errore...</p>',
    condition: (formData) => formData.enpal_entry_type !== 'cer_producer',
  },
];

/**
 * Trova l'indice di una slide per ID
 */
export const getSlideIndexById = (id: string): number => {
  return SLIDES_CONFIG.findIndex((slide) => slide.id === id);
};

/**
 * Slide IDs che mostrano benefici dinamici (hanno header)
 */
export const BENEFIT_SLIDE_IDS = [
  'decision_stage',
  'name_form',
  'email_form',
  'phone_form',
] as const;

/**
 * Slide IDs per il calcolo del progresso
 */
export const PROGRESS_SLIDE_IDS = SLIDES_CONFIG
  .filter((slide) => slide.type === 'SINGLE_ANSWER' || slide.type === 'FORM_INPUT')
  .map((slide) => slide.id);

/**
 * Ottiene la chiave del beneficio per un dato slide ID
 */
export function getBenefitKeyForSlide(slideId: string): string {
  return SLIDE_TO_BENEFIT_KEY[slideId] || slideId;
}

/**
 * Nome breve del beneficio per le CTA (come slider_all_bonus_short_cap)
 */
export function getBenefitShortName(benefitText: string | null): string {
  if (!benefitText) return 'il prossimo Beneficio';

  const text = benefitText.toLowerCase();

  if (text.includes('bolletta')) return 'il Risparmio in Bolletta';
  if (text.includes('surplus')) return 'il Surplus CER';
  if (text.includes('rivendita') && !text.includes('surplus')) return 'la Rivendita Energia';
  if (text.includes('ecobonus')) return "l'Ecobonus";
  if (text.includes('pnrr') || text.includes('cer enpal')) return 'il PNRR CER Enpal';
  if (text.includes('conto termico')) return 'il Conto Termico';
  if (text.includes('bonus regione fvg')) return 'il Bonus Regione FVG';
  if (text.includes('risparmio totale')) return 'il Risparmio Totale';

  return 'il prossimo Beneficio';
}
